/**
 * ErrorBoundary Component
 * Catches React errors and displays the GlobalMessageModal instead of crashing the UI.
 */

'use client';

import React, { Component, ReactNode } from 'react';
import { emitSeqLog, LogLevel } from '@/hooks/telemetry/use-seq-log';
import { ErrorFallback } from './ErrorFallback';

const ERROR_BOUNDARY_SEQ_COMPONENT = 'ErrorBoundary';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * What stands in for the subtree once it has failed.
   *
   * Omitted, the app-level fallback takes over — which is right for the one
   * boundary that wraps the whole app and wrong for a boundary around a single
   * widget: without a nearer boundary, one widget's runtime error is caught up
   * here and the entire page, tab strip included, is replaced. A widget passes
   * its own stand-in so its failure costs its own box and nothing else.
   */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    emitSeqLog({
      component: ERROR_BOUNDARY_SEQ_COMPONENT,
      msg: 'React render crash caught by ErrorBoundary',
      level: LogLevel.Error,
      props: {
        errorName: error.name,
        errorMessage: error.message,
        stack: error.stack ?? null,
        componentStack: errorInfo.componentStack ?? null,
        url: typeof window !== 'undefined' ? window.location.href : null,
      },
    });

    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] React runtime error', {
        error,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // `undefined` and not falsiness: a widget that asks to render NOTHING in
      // its own place passes `null`, and that is a different instruction from
      // saying nothing at all.
      return this.props.fallback === undefined ? (
        <ErrorFallback onReset={this.handleReset} />
      ) : (
        this.props.fallback
      );
    }

    return this.props.children;
  }
}
